import { ALGOS_E } from "@constants/Algos";
import { getDevivedHash, getMaxSpeedTitle, HashFactorName } from "@utils/hash.utils";
import { mskTimeFormat, todayUtc } from "@utils/moscowTime";
import { ChartData, ChartOptions } from "chart.js";
import moment, { Moment } from "moment";
// import { RangeValue } from 'rc-picker/lib/interface';
import { dev } from "@utils/dev";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { useIntl } from "react-intl";
import ChartDatasetUtils, { HashrateItem } from "./ChartDatasetUtils";
import { IChartLiveDataSource } from "./ChartLiveDataSource";
import chartReducer, { ChartActions, createInitialState, IAllData } from "./ChartReducer";
import ChartUtils, { createChartDataset, createChartOptions, isHasData } from "./ChartUtils";
import { appendValueToChartDataset } from "./CleanChart.utils";
import { IChartPeriodControl } from "./CleanChartPeriod.control";
import { ChartLabelsAndData, createChartOptionMethods, getRejectRate } from "./HashChart";
import HoverLinePlugin from "./HoverLineChartPlugin";

export type NullableChartPeriod = [Moment | null, Moment | null];
export type MomentPeriod = [Moment, Moment];
export const ChartPeriodDefaultValue: NullableChartPeriod = [null, null];

export interface ICleanChartValue {
  datasetIndex: number;
  timeInSec: number;
  value: number;
  state: number;
}

interface ICleanChartControl {
  chartRef: MutableRefObject<ChartJSOrUndefined<"line"> | null>;
  shimmerRef: MutableRefObject<HTMLDivElement | null>;
  readonly isShowPeriod: boolean;
  readonly pickerValue: MomentPeriod | null;
  // readonly isLoading: boolean;
  readonly hasData: boolean;
  readonly infoVisibility: boolean;
  readonly isNotEmpty: boolean;
  readonly coords: [number, number];
  readonly lastPoint: [number, number];
  readonly chartOptions: ChartOptions<"line">;
  readonly data: ChartData<"scatter">;
  readonly axisYName: HashFactorName;
  readonly calcSpeedDrops?: number;
  readonly currentDay: Moment | null;
  //readonly period: [Moment, Moment];

  isDayDisabled(day: Moment): boolean;
  onPickerPeriodChanged: (
    value: any
    // RangeValue<Moment>
  ) => void;
  hideInfo: () => void;
  showInfo: () => void;
  setCurrentDay: (value: Moment) => void;
  //
  //enqueueValue(timeSec: number, value: number, datasetIndex: number): void;
}

/*
const (
  ScaleNone = iota
  Scale5M   // 1 default
  Scale15M  // 2
  Scale1H   // 3
  Scale1D   // 4
  Scale3D   // 5
  Scale7D   // 6
  Scale10D  // 7
  Scale30D  // 8
)
*/
export enum ChartScales { // TODO: переименовать
  Scale5M = "5m",
  Scale15M = "15m",
  Scale30M = "30m",
  Scale1H = "1h",
  Scale1D = "1d",
  Scale3D = "3d",
  Scale7D = "7d",
  Scale10D = "10d",
  Scale30D = "30d",
}

// export enum ChartScale { // old TODO: удалить
//   ScaleNone = 0,
//   Scale5M,   // 1 default
//   Scale15M,  // 2
//   Scale1H,   // 3
//   Scale1D,   // 4
//   Scale3D,   // 5
//   Scale7D,   // 6
//   Scale10D,  // 7
//   Scale30D,  // 8
// }

interface IScaleTimes {
  [key: string]: number;
}
const DAY_SECONDS = 24 * 60 * 60;

export const ScaleSeconds: IScaleTimes = {
  [ChartScales.Scale5M]: 5 * 60,
  [ChartScales.Scale15M]: 15 * 60,
  [ChartScales.Scale1H]: 60 * 60,
  [ChartScales.Scale1D]: DAY_SECONDS,
  // [ChartScale.Scale3D]: 3 * DAY_SECONDS,
  // [ChartScale.Scale7D]: 7 * DAY_SECONDS,
  // [ChartScale.Scale10D]: 10 * DAY_SECONDS,
  // [ChartScale.Scale30D]: 30 * DAY_SECONDS,
};

// export interface CleanChartDataset {
//   period?: NullableChartPeriod;
//   label?: string;
//   data: Array<number[][]>;
// }

export interface CleanChartDataset {
  period?: NullableChartPeriod;
  label?: string;
  data: HashrateItem[];// | number[][];
}

interface CleanChartProps<T> {
  id?: number,
  scale?: ChartScales | null,
  showLegend: boolean;
  showY1Scales: boolean;
  liveDataSource: IChartLiveDataSource<T>;
  rawDatasets: CleanChartDataset[];
  poolHash: ALGOS_E;
  speedDrops?: number,
  defaultSpeed?: number,
  tag?: string;
  readonly periodControl: IChartPeriodControl;
  readonly visibilityTimeoutMs?: number;
  onVisible?: () => void;
}

function getRequestPeriod(period: NullableChartPeriod): MomentPeriod {
  //const result: MomentPeriod = [0 , 0];
  if (period[0] === null || period[1] === null) {
    return [todayUtc().startOf("day"), todayUtc().add(1, "day").startOf("day")];
  }
  return [
    period[0].clone().startOf("day"),
    period[1].clone().add(1, "day").startOf("day"),
  ];
}

function useCleanChartControl<T>({
  id,
  scale,
  showLegend,
  showY1Scales,
  rawDatasets,
  poolHash,
  periodControl,
  liveDataSource,
  speedDrops,
  defaultSpeed,
  visibilityTimeoutMs,
  tag,
  onVisible
}: CleanChartProps<T>) {

  const chartRef = useRef<ChartJSOrUndefined<"line"> | null>(null);
  const shimmerRef = useRef<HTMLDivElement | null>(null);
  const { formatMessage } = useIntl();
  //const [isShowScale, setShowScale] = useState(true);

  const [state, updateState] = useReducer(
    chartReducer,
    createInitialState(0)
  );

  useEffect(() => {
    liveDataSource.start(appendToChart, onDayChanged);
    return () => {
      liveDataSource.stop();
    }
  }, [scale]);

  useEffect(() => {
    if (rawDatasets.length > 0) {
      onGetHashrates(rawDatasets[0].data);
    }
  }, [rawDatasets]);

  useEffect(() => {
    if (onVisible !== undefined) {
      document.addEventListener("visibilitychange", onVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    }
  }, [onVisible]);

  const onVisibilityChange = useCallback(
    (e: Event) => {
      if (document.visibilityState === "visible") {
        const sleepTime = localStorage.getItem("cleanchart-sleep");
        if (sleepTime !== null) {
          const diff = Date.now() - parseInt(sleepTime);
          if (diff > (visibilityTimeoutMs ?? 300000)) {
            if (onVisible) onVisible();
          }
        }
      }
      if (document.visibilityState === "hidden") {
        localStorage.setItem("cleanchart-sleep", `${Date.now()}`);
      }
    },
    [visibilityTimeoutMs, onVisible]
  );

  const onLastValueChanged = useCallback(() => {
    updateState({
      type: ChartActions.SET_COORDS_AND_LAST_POINT,
      payload: {
        coords: ChartUtils.getCoords(chartRef.current),
        lastPoint: ChartUtils.getLastPoint(chartRef.current),
      },
    });
  }, [state]);

  const onDayChanged = (value: Moment) => {
    periodControl.updateDay(value, "onDayChanged");
  };

  const onTicksChanged = () => {
    const shimmer = shimmerRef.current;
    if (shimmer) {
      shimmer.style.zIndex = "10";
      shimmer.style.opacity = "1";
      setTimeout(() => {
        shimmer.style.opacity = "0";
        shimmer.style.zIndex = "-1";
      }, 1000);
    }
  };

  function onAnimationEnd() {
    setTimeout(() => {
      const hasData = !ChartUtils.isEmpty(chartRef.current);
      const isNotEmpty = hasData && ChartUtils.isNonZeroChart(chartRef.current);
      updateState({
        type: ChartActions.SET_COORDS_AND_LAST_POINT,
        payload: {
          coords: ChartUtils.getCoords(chartRef.current),
          lastPoint: ChartUtils.getLastPoint(chartRef.current),
        },
      });
      updateState({
        type: ChartActions.SET_IS_NOT_EMPTY,
        payload: isNotEmpty,
      });
      updateState({
        type: ChartActions.SET_HAS_DATA,
        payload: hasData,
      });
    }, 200);
  }

  // const chartOptions = useMemo(() => createChartOptionMethods(
  //   onTicksChanged,
  //   state.axisYName,
  //   speedDrops,
  //   defaultSpeed
  // ), [speedDrops, defaultSpeed, state.axisYName]);

  const onGetHashrates = (raw: HashrateItem[] | number[][]) => {
    const realPeriod: MomentPeriod = getRequestPeriod(periodControl.period);

    let chartDataAndLabels: ChartLabelsAndData = {
      labels: [],
      data: [],
      status: [],
      accepted: [],
      rejected: [],
    };

    if (raw && raw.length > 0) {
      const firstElement = raw[0];
      if (Array.isArray(firstElement)) { // numbers
        chartDataAndLabels = ChartDatasetUtils.transformResponseToChartData(raw as number[][]);
      } else {
        chartDataAndLabels = ChartDatasetUtils.transformResponseToChartData2(raw as HashrateItem[]);
      }
    }

    if (defaultSpeed !== undefined && speedDrops !== undefined) {
      chartDataAndLabels = ChartDatasetUtils.appendBounds(
        chartDataAndLabels,
        defaultSpeed,
        speedDrops
      );
    }

    const dataSet = createChartDataset({
      label: formatMessage({ id: "chart.hashrate" }),
      data: chartDataAndLabels.data,
      defaultSpeed,
      speedDrops,
    });
    (dataSet as any).lineTension = 0.01;
    (dataSet as any).order = 100;

    const chartData: ChartData<"line"> = {
      labels: chartDataAndLabels.labels,
      datasets: [dataSet],
    };
    if (dataSet.data.length === 0) {
      chartData.labels = ChartUtils.createEmptyLabelsForPeriod(realPeriod);
      for (let i = 0; i < chartData.labels.length; i++) {
        chartData.datasets[0].data.push(NaN);
      }
    }
    // append TODO: создание датасетов не должно быть здеnhсь...
    const plugins = [];
    if (chartDataAndLabels.rejected.length > 0 && chartDataAndLabels.accepted.length > 0) {
      const rejectRates: number[] = [];
      for (let i = 0; i < chartDataAndLabels.rejected.length; i++) {
        rejectRates.push(
          getRejectRate(
            chartDataAndLabels.rejected[i],
            chartDataAndLabels.accepted[i]
          )
        );
      }
      chartData.datasets.push({
        type: 'line',
        label: formatMessage({ id: "chart.reject_rate" }),
        borderWidth: 2,
        borderColor: 'rgba(100, 0, 0, .6)',
        backgroundColor: 'rgba(100, 0, 0, .6)',
        data: rejectRates,
        order: 1,
        yAxisID: "y1",
      });
      plugins.push(HoverLinePlugin);
    }
    const hasData = isHasData(dataSet.data as number[]);
    // let higher = higherHashDecimal(
    //   chartDataAndLabels.data,
    //   poolHash
    // ); //[0] as HashFactorName;
    const maxAndTitle = getMaxSpeedTitle(
      chartDataAndLabels.data,
      poolHash
    );

    // if (higher && higher.length > 0) {
    //   higher = higher[0] as HashFactorName;
    // } else {
    //   higher = HashFactorName.N;
    // }
    const options = createChartOptions(
      showLegend,
      showY1Scales,
      createChartOptionMethods(
        onTicksChanged,
        maxAndTitle[1], // as HashFactorName, //state.axisYName,
        speedDrops,
        defaultSpeed
      ),
      onAnimationEnd,
      Math.abs(realPeriod[1].diff(realPeriod[0], "days")),
      poolHash
    );

    updateState({
      type: ChartActions.SET_ALL_DATA,
      payload: {
        lastPoint: [0, 0, 0],
        chartData,
        chartOptions: options,
        hasData: hasData,
        selfIsLoading: false,
        axisYName: maxAndTitle[1],
        plugins,
      } as IAllData,
    });
  };

  const exportToCSV = useCallback((minerName: string) => {
    const { datasets, labels } = ChartUtils.getChartData(chartRef.current) || {};
    if (!datasets || !labels) {
      return;
    }
    if (datasets.length === 0) {
      return;
    }
    let csvContent = "Date, Value\n";
    if (datasets.length > 0) {
      for (let i = 0; i < datasets[0].data.length; i++) {
        const value = datasets[0].data[i];
        const time = mskTimeFormat(labels[i], "DD/MM/YY HH:mm:ss");
        csvContent += `${time}, ${getDevivedHash(value, { unit: poolHash })}\n`;
      }
    }
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${minerName}-values.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  }, [scale, tag, periodControl.period]);

  const onPickerPeriodChanged = (
    values: any
    // RangeValue<Moment>
  ) => {
    dev.logt("CleanChart", "onPickerPeriodChanged", {
      values,
      prev: periodControl.period
    });
    if (!values) {
      periodControl.clearPeriod();
    } else {
      periodControl.updatePeriod([values[0] as Moment, values[1] as Moment]);
    }
  };

  const isDayDisabled = (currentDate: Moment) => currentDate.isAfter(moment());

  const showInfo = () => {
    updateState({
      type: ChartActions.SET_INFO_VISIBILITY,
      payload: true,
    });
  };

  const hideInfo = () => {
    updateState({
      type: ChartActions.SET_INFO_VISIBILITY,
      payload: false,
    });
  };

  function setCurrentDay(value: Moment) {
    periodControl.updateDay(value, "setCurrentDay");
  }

  const appendToChart = useCallback((
    value: ICleanChartValue,
    countRef: MutableRefObject<number>
  ) => {
    const { datasets, labels } = ChartUtils.getChartData(chartRef.current) || {};
    if (!datasets || !labels) {
      return;
    }
    if (datasets.length === 0) {
      return;
    }
    console.log("appendToChart", { datasets, value });
    if (isSameDay(value.timeInSec * 1000, labels[labels.length - 1])) {
      const dataset = datasets[value.datasetIndex];
      appendValueToChartDataset(
        dataset,
        labels,
        value.timeInSec,
        value.value,
        ScaleSeconds[scale || ChartScales.Scale5M],
        countRef,
      );
      onLastValueChanged();
      chartRef.current?.update("none");
    }
  }, [scale, tag]);

  function isSameDay(
    timestampMs1: number,
    timestampMs2: number
  ): boolean {
    return moment(timestampMs1).utc()
      .isSame(moment(timestampMs2).utc(), "day");
  }

  return {
    chartRef,
    shimmerRef,
    data: state.chartData,
    pickerValue: periodControl.pickerValue,
    currentDay: periodControl.currentDay,
    ...state,
    isDayDisabled,
    onPickerPeriodChanged,
    showInfo,
    hideInfo,
    setCurrentDay,
    exportToCSV,
  };
}

export default useCleanChartControl;
